import { useEffect, useState, useCallback } from 'react';
import { quotesAPI } from '../services/api';
import { Table, Badge, Btn, Modal, Card, Pagination, useToast, Input, Select, Spinner } from '../components/UI';

export default function Quotes() {
  const { show, ToastEl } = useToast();
  const [quotes, setQuotes]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [meta, setMeta]           = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [editQuote, setEditQuote] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await quotesAPI.list({ page, limit: 15 });
      setQuotes(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load quotes', 'error'); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  async function deleteQuote(id) {
    if (!window.confirm(`Delete this quote? This cannot be undone.`)) return;
    try {
      await quotesAPI.delete(id);
      show('Quote deleted');
      load();
    } catch (e) { show(e.response?.data?.message || 'Failed to delete', 'error'); }
  }

  const columns = [
    { key: 'text', label: 'Quote', render: (v) => (
      <div style={{ maxWidth: 500, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{v}</div>
    )},
    { key: 'author', label: 'Author', render: (v) => v || <span style={{ color: 'var(--muted)' }}>—</span> },
    { key: 'source', label: 'Source', render: (v) => v || <span style={{ color: 'var(--muted)' }}>—</span> },
    { key: 'status', label: 'Status', render: (v) => (
      <Badge color={v === 'active' ? 'green' : 'orange'}>{v}</Badge>
    )},
    { key: 'created_by', label: 'Added by' },
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Btn size="sm" onClick={() => setEditQuote(row)}>Edit</Btn>
        <Btn size="sm" variant="danger" onClick={() => deleteQuote(row.id)}>Delete</Btn>
      </div>
    )},
  ];

  return (
    <div style={{ padding: 28 }}>
      {ToastEl}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Islamic Quotes</h1>
        <Btn onClick={() => setShowCreate(true)}>+ Add Quote</Btn>
      </div>

      <Card>
        {loading
          ? <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
          : <Table columns={columns} data={quotes} />
        }
        {!loading && meta.pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <Pagination page={page} pages={meta.pages} onChange={setPage} />
          </div>
        )}
      </Card>

      {(showCreate || editQuote) && (
        <QuoteModal
          quote={editQuote}
          onClose={() => { setShowCreate(false); setEditQuote(null); }}
          onSaved={() => { setShowCreate(false); setEditQuote(null); load(); show(editQuote ? 'Quote updated' : 'Quote added'); }}
          onError={(msg) => show(msg, 'error')}
        />
      )}
    </div>
  );
}

function QuoteModal({ quote, onClose, onSaved, onError }) {
  const [form, setForm] = useState({
    text:   quote?.text   || '',
    author: quote?.author || '',
    source: quote?.source || '',
    status: quote?.status || 'active',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.text.trim()) { onError('Quote text is required'); return; }
    setSaving(true);
    try {
      const body = {
        text:   form.text.trim(),
        author: form.author.trim() || null,
        source: form.source.trim() || null,
        status: form.status,
      };
      if (quote) {
        await quotesAPI.update(quote.id, body);
      } else {
        await quotesAPI.create(body);
      }
      onSaved();
    } catch (e) {
      onError(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={quote ? 'Edit Quote' : 'Add Quote'} onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: 'var(--muted)' }}>
              QUOTE TEXT *
            </label>
            <textarea
              value={form.text}
              onChange={e => set('text', e.target.value)}
              rows={5}
              required
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 10px', borderRadius: 7,
                border: '1px solid var(--border)',
                fontSize: 14, lineHeight: 1.6,
                fontFamily: 'inherit', resize: 'vertical',
                background: 'var(--bg)',
                color: 'var(--text)',
              }}
            />
          </div>

          <Input
            label="Author (optional)"
            value={form.author}
            onChange={e => set('author', e.target.value)}
            placeholder="e.g. Rumi, Ibn Ata Allah"
          />

          <Input
            label="Source / Book (optional)"
            value={form.source}
            onChange={e => set('source', e.target.value)}
            placeholder="e.g. Masnavi, Al-Hikam"
          />

          <Select
            label="Status"
            value={form.status}
            onChange={e => set('status', e.target.value)}
            options={[
              { value: 'active',   label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>
              {saving ? 'Saving…' : quote ? 'Save changes' : 'Add Quote'}
            </Btn>
          </div>
        </div>
      </form>
    </Modal>
  );
}
